import Head from 'next/head';
import { NextSeo } from 'next-seo';
import urljoin from 'url-join';

import { SEO as SEOType, Page } from '@/types';
import { defaultSeoUrl } from '../../next-seo.config';

type SEOProps = Pick<Page, 'title' | 'subTitle' | 'coverImage'> & {
  meta: SEOType;
};

export default function SEO({
  title,
  subTitle,
  meta,
  coverImage,
}: Partial<SEOProps>) {
  const {
    description: metaDescription,
    keywords: metaKeywords,
    noindex,
    nofollow,
    title: metaTitle,
    image,
  } = meta || {};

  const pageTitle = metaTitle || title;
  const pageDescription = metaDescription || subTitle;
  const pageImage =
    image?.url || urljoin(defaultSeoUrl, `/images/og-fallback.png`);

  return (
    <>
      <NextSeo
        titleTemplate="%s | GraphCMS"
        title={pageTitle}
        description={pageDescription}
        additionalMetaTags={[
          {
            property: `keywords`,
            content:
              metaKeywords && metaKeywords.length > 0
                ? metaKeywords[0]
                : `Headless CMS, GraphCMS, GraphQL CMS, API CMS`,
          },
        ]}
        openGraph={{
          title: pageTitle,
          description: pageDescription,
          type: `website`,
          images: [
            {
              url: pageImage,
              width: image?.width || 1280,
              height: image?.height || 720,
            },
          ],
          site_name: `GraphCMS`,
        }}
        {...(!!noindex && { noindex: true })}
        {...(!!noindex && !!nofollow && { nofollow: true })}
      />

      <Head>
        <meta name="twitter:title" content={pageTitle || `GraphCMS`} />
        <meta name="twitter:image" content={pageImage} />

        <meta
          property="og:image:type"
          content={
            coverImage && coverImage.mimeType
              ? coverImage.mimeType
              : `image/png`
          }
        />

        <script type="application/ld+json">
          {`{
          "@context": "https://schema.org",
          "@type": "Organization",
          "url": "https://graphcms.com",
          "logo": "https://graphcms.com/logo.svg"
        }`}
        </script>
      </Head>
    </>
  );
}
